'use client';

import {
  WifiOff, RefreshCw, ShieldCheck, Layers, Cloud, Smartphone,
  ArrowRight, Server, Database, CheckCircle2, Zap, Lock, AlertTriangle,
  Monitor, Tablet, Globe,
  type LucideIcon,
} from 'lucide-react';
import { LocaleLink } from '@/core/i18n';
import { FAQSection } from './FAQSection';
import { Testimonials } from './Testimonials';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Static copy — pure SSG, zero API calls (per public-page rule)              */
/* -------------------------------------------------------------------------- */

interface CapabilityCard { id: string; icon: LucideIcon; title: string; description: string }

const CAPABILITIES: CapabilityCard[] = [
  { id: 'offline-capable-pwa', icon: Smartphone, title: 'Offline-Capable PWA', description: 'Install the front desk as a Progressive Web App. Once cached, check-ins, check-outs, housekeeping updates, and room-service orders keep working even when the network drops to zero.' },
  { id: 'mutation-queue', icon: Database, title: 'Offline Mutation Queue', description: 'Every write made while offline is captured in a local IndexedDB store with timestamps, operator IDs, and idempotency keys. Nothing is lost between the browser and the server.' },
  { id: 'background-sync', icon: RefreshCw, title: 'Background Sync on Reconnect', description: 'When connectivity returns, the service worker replays the queue through the official backend in original order. Conflicts are detected against server state, not silently overwritten.' },
  { id: 'conflict-reconciliation', icon: ShieldCheck, title: 'Conflict-Aware Reconciliation', description: 'Race conditions across staff tabs are surfaced, not buried. Each merged write returns a verdict: applied, merged, or flagged for manager review with an audit log entry.' },
  { id: 'service-worker-shell', icon: Layers, title: 'Service-Worker App Shell', description: 'The hotel-stack routes you visit are cached on first load using a stale-while-revalidate strategy. Returning to a route while offline renders instantly from cache while updates sync in the background.' },
  { id: 'multi-tab-coordination', icon: Zap, title: 'Multi-Tab Coordination', description: 'BroadcastChannel elects a single sync leader across tabs. Avoids duplicate replays when three front-desk terminals are open simultaneously and the network returns.' },
];

interface FlowStep { id: string; icon: LucideIcon; title: string; description: string }

const FLOW_STEPS: FlowStep[] = [
  { id: 'discover', icon: WifiOff, title: '1. Connectivity Drops', description: 'A worker unplugs a network cable or the ISP has an outage. The PWA detects the loss via navigator.onLine and the service worker intercepts fetch events to serve cached responses.' },
  { id: 'queue', icon: Database, title: '2. Writes Queued Locally', description: 'Mutations are written to IndexedDB with an idempotency key, operator ID, and monotonic local timestamp. The UI shows the queue depth so staff know their work is captured.' },
  { id: 'work', icon: CheckCircle2, title: '3. Operations Continue', description: 'Check-ins, check-outs, room moves, housekeeping task completions, and room-service orders all complete against the local cache. Guest-facing screens keep showing confirmed bookings.' },
  { id: 'reconcile', icon: Cloud, title: '4. Reconnect and Reconcile', description: 'On navigator.online, the leader tab drains the queue via Background Sync. Each request is replayed against the server. Server responses are merged, conflicts are flagged in the audit log, and the UI updates.' },
];

const FAQ_ITEMS = [
  { id: 'res-faq-1', question: 'What happens if two staff members edit the same reservation while offline?', answer: 'Both writes are queued. When the network returns, the leader tab replays them in original timestamp order. If the server detects a conflict (e.g., the same room was reassigned remotely), both operators see a flagged conflict with full audit context and a manager must approve the resolution.' },
  { id: 'res-faq-2', question: 'How long does the offline queue survive?', answer: 'The queue persists in IndexedDB until the browser clears site data. HotelStack does not impose a TTL — a queue item queued in the morning will still be present a week later if connectivity has not returned. Capacity is bounded only by disk space.' },
  { id: 'res-faq-3', question: 'Can I install the front desk on a tablet?', answer: 'Yes. The PWA installs from Chrome on Android, Edge on Windows, and Safari on iPadOS. Once installed it runs full-screen with its own icon, splash screen, and works without a network connection. iOS Safari requires Add to Home Screen from the share menu.' },
  { id: 'res-faq-4', question: 'Does offline mode work for guest-facing booking pages too?', answer: 'Yes. BookMyRoom.io pages cache their last-rendered hotel list, search results, and itinerary details. A guest who arrives at the airport without data can still open BookMyRoom, view their upcoming reservation, and find their hotel address.' },
  { id: 'res-faq-5', question: 'Is there extra cost for offline mode?', answer: 'No. The offline-capable PWA, mutation queue, and background sync are included in Professional and Enterprise plans at no additional cost. Starter plans receive a standard PWA install for browsing only.' },
];

const TESTIMONIAL_QUOTES = [
  { id: 'res-test-1', quote: 'Our front desk operates from a heritage building where the basement reception desk loses Wi-Fi during heavy rain. The PWA switchover is invisible — staff keep checking guests in and the queue replays the moment the access point comes back.', author: 'Operations Manager, 92-room heritage hotel (UK)' },
  { id: 'res-test-2', quote: 'We run a safari camp in the Serengeti with satellite internet that drops three or four times a day. HotelStack\'s offline queue is the only reason housekeeping updates ever reach the back office. We have not lost a single task in eight months.', author: 'General Manager, remote safari camp (Tanzania)' },
  { id: 'res-test-3', quote: 'During the 2026 regional power-grid event, our coastal property ran the front desk on generator power for 11 hours. Because the PWA caches the room board, we still checked in 17 arrivals on tablets without ever touching the main server.', author: 'Director of Operations, 240-room resort (Philippines)' },
];

/* -------------------------------------------------------------------------- */
/*  Hero                                                                       */
/* -------------------------------------------------------------------------- */

function Hero() {
  return (
    <section id="resilience-hero" className="relative w-full overflow-hidden bg-hms-bg">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(13,158,153,0.10) 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
      <div className="relative mx-auto max-w-5xl px-4 pb-20 pt-24 sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <div id="resilience-hero-badge" className="mx-auto flex w-fit items-center gap-2 rounded-full border border-hms-teal/30 bg-hms-teal/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-hms-teal">
          <ShieldCheck className="h-3.5 w-3.5" />
          Hybrid Resilience Layer
        </div>
        <h1 id="resilience-hero-title" className="mt-6 text-center text-4xl font-extrabold leading-tight tracking-tight text-hms-text sm:text-5xl lg:text-6xl">
          <span className="bg-gradient-to-r from-hms-teal via-hms-gold to-hms-goldLt bg-clip-text text-transparent">Resilience & Offline Operations</span>
        </h1>
        <p id="resilience-hero-subtitle" className="mx-auto mt-6 max-w-3xl text-center text-base text-hms-textSub sm:text-lg">
          HotelStack.io is a cloud-native PMS with a built-in hybrid resilience layer. The front desk installs as a Progressive Web App and keeps working through network drops, ISP outages, and dead-zone basements. Mutations are queued locally, replayed in order, and reconciled against the server with full audit context.
        </p>
        <div id="resilience-hero-actions" className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink id="resilience-hero-trial-btn" href="/register-hotel" className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}>
            Start Free Trial
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink id="resilience-hero-pricing-btn" href="/pricing" className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}>
            View Pricing
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Capabilities grid                                                          */
/* -------------------------------------------------------------------------- */

function Capabilities() {
  return (
    <section id="resilience-capabilities" className="bg-hms-bg py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 id="resilience-capabilities-title" className="text-3xl font-extrabold tracking-tight text-hms-text sm:text-4xl">
            <span className="bg-gradient-to-r from-hms-gold to-hms-goldLt bg-clip-text text-transparent">Six Capabilities, One Resilience Stack</span>
          </h2>
          <p id="resilience-capabilities-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            HotelStack.io ships with a complete offline layer — not a bolt-on. Every capability below is included in Professional and Enterprise plans.
          </p>
        </div>
        <div id="resilience-capabilities-grid" className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((c, i) => (
            <div key={c.id} id={c.id} className={cn('group relative rounded-xl border border-hms-border bg-hms-card p-6', 'transition-all duration-300', 'hover:border-hms-teal/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20')}>
              <div className={cn('mb-4 flex h-11 w-11 items-center justify-center rounded-lg', 'bg-hms-teal/10 transition-colors duration-300 group-hover:bg-hms-teal/15')}>
                <c.icon className="h-5 w-5 text-hms-teal" />
              </div>
              <h3 className="mb-2 text-base font-bold tracking-tight text-hms-text">{c.title}</h3>
              <p className="text-[13px] leading-relaxed text-hms-textSub">{c.description}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em] text-hms-textDim">
                <span>Capability {String(i + 1).padStart(2, '0')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Flow steps                                                                 */
/* -------------------------------------------------------------------------- */

function Flow() {
  return (
    <section id="resilience-flow" className="bg-hms-surface py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 id="resilience-flow-title" className="text-3xl font-extrabold tracking-tight text-hms-text sm:text-4xl">
            <span className="bg-gradient-to-r from-hms-gold to-hms-goldLt bg-clip-text text-transparent">How an Outage Is Handled</span>
          </h2>
          <p id="resilience-flow-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            Four steps from connectivity loss to clean reconciliation. Every step is event-driven — no polling, no timeouts.
          </p>
        </div>
        <div id="resilience-flow-grid" className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {FLOW_STEPS.map((s) => (
            <div key={s.id} id={s.id} className={cn('relative rounded-xl border border-hms-border bg-hms-card p-6', 'transition-all duration-300 hover:border-hms-gold/20')}>
              <div className={cn('mb-4 flex h-10 w-10 items-center justify-center rounded-lg', 'bg-hms-gold/10')}>
                <s.icon className="h-5 w-5 text-hms-gold" />
              </div>
              <h3 className="mb-2 text-[15px] font-bold tracking-tight text-hms-text">{s.title}</h3>
              <p className="text-[13px] leading-relaxed text-hms-textSub">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Architecture overview                                                       */
/* -------------------------------------------------------------------------- */

function Architecture() {
  const layers = [
    { id: 'arch-tab', icon: Smartphone, label: 'Front Desk PWA', description: 'Installable on Android, Windows, iPadOS. Caches app shell, room board, and reservation detail routes.' },
    { id: 'arch-sw', icon: Layers, label: 'Service Worker', description: 'Intercepts fetch events. Implements stale-while-revalidate for routes, network-first for API reads, and queue-and-replay for API writes.' },
    { id: 'arch-idb', icon: Database, label: 'IndexedDB Store', description: 'Holds the offline mutation queue and the TanStack Query cache. Persists across browser restarts and tab closures.' },
    { id: 'arch-bus', icon: Zap, label: 'BroadcastChannel Sync Bus', description: 'Elects a single sync leader across open tabs. Drains the queue without duplicate replays.' },
    { id: 'arch-server', icon: Server, label: 'HotelStack Backend', description: 'Receives the replay. Performs server-side conflict checks. Returns merged state to the leader which fans out updates to every tab.' },
  ];
  return (
    <section id="resilience-architecture" className="bg-hms-bg py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 id="resilience-architecture-title" className="text-3xl font-extrabold tracking-tight text-hms-text sm:text-4xl">
            <span className="bg-gradient-to-r from-hms-teal via-hms-gold to-hms-goldLt bg-clip-text text-transparent">The Resilience Stack</span>
          </h2>
          <p id="resilience-architecture-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            Five layers from tablet to backend. Every layer is enterprise-grade and event-driven.
          </p>
        </div>
        <div id="resilience-architecture-stack" className="mx-auto mt-14 max-w-4xl space-y-3">
          {layers.map((l, i) => (
            <div key={l.id} id={l.id} className={cn('flex items-start gap-5 rounded-xl border border-hms-border bg-hms-card p-5', 'transition-all duration-300 hover:border-hms-gold/20')}>
              <div className="flex flex-col items-center gap-2 pt-1">
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-hms-gold/30 bg-hms-gold/10 text-[12px] font-bold text-hms-gold">{i + 1}</span>
                <l.icon className="h-5 w-5 text-hms-teal" />
              </div>
              <div className="flex-1">
                <h3 className="text-[15px] font-bold tracking-tight text-hms-text">{l.label}</h3>
                <p className="mt-1 text-[13px] leading-relaxed text-hms-textSub">{l.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Audit guarantee panel                                                      */
/* -------------------------------------------------------------------------- */

function AuditPanel() {
  const guarantees = [
    { id: 'audit-otel', icon: Lock, label: 'OTEL-structured logs', description: 'Every queued write, replay attempt, conflict verdict, and queue drain is logged with trace_id, span_id, operator_id, timestamp, and outcome.' },
    { id: 'audit-no-loss', icon: CheckCircle2, label: 'No silent drops', description: 'If a replay returns a non-2xx status, the item stays in the queue and surfaces in the dashboard. There is no silent failure path.' },
    { id: 'audit-conflict', icon: AlertTriangle, label: 'Explicit conflicts', description: 'Conflicts are surfaced to managers with both versions and original timestamps. The default is to flag, not overwrite.' },
  ];
  return (
    <section id="resilience-audit" className="bg-hms-surface py-20 sm:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-hms-border bg-hms-card p-8 sm:p-12">
          <h2 id="resilience-audit-title" className="text-2xl font-extrabold tracking-tight text-hms-text sm:text-3xl">
            Audit &amp; Observability
          </h2>
          <p id="resilience-audit-subtitle" className="mt-4 text-[15px] leading-relaxed text-hms-textSub">
            A resilience layer that hides failures is worse than no resilience at all. HotelStack.io logs every offline outcome to the central audit log with operator context, so post-incident reviews are factual rather than reconstructed.
          </p>
          <div id="resilience-audit-grid" className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {guarantees.map((g) => (
              <div key={g.id} id={g.id} className="rounded-xl border border-hms-border bg-hms-bg p-5">
                <g.icon className="h-5 w-5 text-hms-teal" />
                <h3 className="mt-3 text-sm font-bold text-hms-text">{g.label}</h3>
                <p className="mt-2 text-[12.5px] leading-relaxed text-hms-textSub">{g.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  PWA Install Matrix                                                         */
/* -------------------------------------------------------------------------- */

interface PlatformRow { id: string; icon: LucideIcon; platform: string; install: string; offline: string; sync: string }

// Claims here must match public/sw.js. The queue is flushed by a SYNC_QUEUE
// message from the page (sw.js:373-376), NOT the Background Sync API — there
// is no 'sync' event listener registered. That makes the flush behaviour the
// same on every engine: queued writes replay when the app is next open and
// online. Do not describe per-platform sync differences we do not implement.
const PLATFORM_ROWS: PlatformRow[] = [
  { id: 'pwa-chromium', icon: Monitor, platform: 'Chrome / Edge (desktop)', install: 'One-click install from address bar', offline: 'Full', sync: 'Replays when app is next open' },
  { id: 'pwa-android', icon: Smartphone, platform: 'Chrome / Edge (Android)', install: 'Add to Home Screen prompt', offline: 'Full', sync: 'Replays when app is next open' },
  { id: 'pwa-ios', icon: Tablet, platform: 'Safari (iOS / iPadOS)', install: 'Share > Add to Home Screen', offline: 'Full (cached shell)', sync: 'Replays when app is next open' },
  { id: 'pwa-firefox', icon: Globe, platform: 'Firefox (desktop / Android)', install: 'Not supported (use as web app)', offline: 'Service Worker cache only', sync: 'Replays when app is next open' },
];

function PWAInstallMatrix() {
  return (
    <section id="resilience-pwa-matrix" className="bg-hms-bg py-20 sm:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 id="resilience-pwa-matrix-title" className="text-3xl font-extrabold tracking-tight text-hms-text sm:text-4xl">
            <span className="bg-gradient-to-r from-hms-gold to-hms-goldLt bg-clip-text text-transparent">PWA Install Matrix</span>
          </h2>
          <p id="resilience-pwa-matrix-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            HotelStack installs as a native-feeling app on every major platform. Offline capability and sync strategy vary by browser engine.
          </p>
        </div>
        <div className="mt-12 overflow-x-auto rounded-xl border border-hms-border">
          <table id="resilience-pwa-matrix-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-hms-border bg-hms-card">
                <th className="px-5 py-3.5 text-[13px] font-bold uppercase tracking-wider text-hms-textSub">Platform</th>
                <th className="px-5 py-3.5 text-[13px] font-bold uppercase tracking-wider text-hms-textSub">Install Method</th>
                <th className="px-5 py-3.5 text-[13px] font-bold uppercase tracking-wider text-hms-textSub">Offline</th>
                <th className="px-5 py-3.5 text-[13px] font-bold uppercase tracking-wider text-hms-textSub">Sync Strategy</th>
              </tr>
            </thead>
            <tbody>
              {PLATFORM_ROWS.map((row) => (
                <tr key={row.id} id={row.id} className="border-b border-hms-border/50 last:border-0">
                  <td className="flex items-center gap-2.5 px-5 py-3.5 font-medium text-hms-text">
                    <row.icon className="h-4 w-4 text-hms-teal" />
                    {row.platform}
                  </td>
                  <td className="px-5 py-3.5 text-hms-textSub">{row.install}</td>
                  <td className="px-5 py-3.5 text-hms-textSub">{row.offline}</td>
                  <td className="px-5 py-3.5 text-hms-textSub">{row.sync}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  FAQ + Testimonials + CTA                                                   */
/* -------------------------------------------------------------------------- */

function FAQBlock() {
  return (
    <FAQSection
      id="resilience-faq"
      title="Resilience FAQ"
      subtitle="Common questions about offline mode, queues, and conflict resolution"
      items={FAQ_ITEMS.map((f) => ({ id: f.id, question: f.question, answer: f.answer }))}
    />
  );
}

function TestimonialsBlock() {
  const quotes = TESTIMONIAL_QUOTES.map((q) => ({ id: q.id, quote: q.quote, author: q.author }));
  return (
    <Testimonials
      title="Resilience in the Wild"
      subtitle="Three properties, three very different connectivity realities — same offline behaviour"
      quotes={quotes}
      sectionId="resilience-testimonials"
    />
  );
}

function CTA() {
  return (
    <section id="resilience-cta" className="bg-hms-bg py-20 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <h2 id="resilience-cta-title" className="text-3xl font-extrabold tracking-tight text-hms-text sm:text-4xl">
          <span className="bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent">Try HotelStack.io Offline-First</span>
        </h2>
        <p id="resilience-cta-subtitle" className="mx-auto mt-5 max-w-2xl text-base text-hms-textSub sm:text-lg">
          Sign up for a free trial, install the front desk on a tablet, and pull the network cable. We will wait on the other side.
        </p>
        <div id="resilience-cta-actions" className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink id="resilience-cta-trial-btn" href="/register-hotel" className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}>
            Start Free Trial
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink id="resilience-cta-demo-btn" href="/contact" className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}>
            Book a Demo
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export function ResiliencePage() {
  const { t } = useTranslation();
  return (
    <main id="resilience-page" className="min-h-screen bg-hms-bg text-hms-text" data-component="resilience-page">
      <Hero />
      <Capabilities />
      <Flow />
      <Architecture />
      <AuditPanel />
      <PWAInstallMatrix />
      <FAQBlock />
      <TestimonialsBlock />
      <CTA />
    </main>
  );
}

export default ResiliencePage;
